import { createRouter, createWebHistory } from 'vue-router'
import store from '../store'
import WelcomeCompany from '@/views/WelcomeCompany.vue'
import CreateCompany from '@/views/CreateCompany.vue'
import Dashboard from '../views/Dashboard.vue'
import Overview from '../views/dashboard/Overview.vue'
import GmailCallback from '../views/GmailCallback.vue'
import { inventoryRoutes } from '../modules/inventory'
import { customerRoutes } from '../modules/customer'
import { vendorRoutes } from '../modules/vendor'
import { settingsRoutes } from '../modules/settings'
import { consultantRoutes } from '../modules/consultant'
import { reportsRoutes } from '../modules/reports'
import { bankingRoutes } from '../modules/banking'
import { companyRoutes } from '../modules/company'
import logger from '@/utils/logger';
import { isAdminJwtExpired, clearAdminSession } from '@/utils/adminSession';
import pageWindowNavigationService from '@/utils/pageWindowNavigation.service';
import { logReportVisitFromRoute } from '@/modules/reports/utils/reportActivity';
import ClientManagerLayout from '../modules/Manager/views/ClientManagerLayout.vue';
import ClientManagerDashboard from '../modules/Manager/views/ClientManagerDashboard.vue';
import ManagerCompanyRecordsLayout from '../modules/Manager/views/ManagerCompanyRecordsLayout.vue';
import ManagerCompanyRecordsView from '../modules/Manager/views/ManagerCompanyRecordsView.vue';

/** Same layout, same client/company — skip global ShimmerLoader on tab change */
const MANAGER_COMPANY_RECORD_ROUTE_NAMES = new Set([
    'ManagerCompanyConsultants',
    'ManagerCompanyCustomers',
    'ManagerCompanyVendors',
    'ManagerCompanyInvoices',
    'ManagerCompanyTimesheets',
    'ManagerCompanyBills'
]);

function isManagerCompanyRecordsTabSwitch(from, to) {
    if (!from?.name || !to?.name) return false;
    if (!MANAGER_COMPANY_RECORD_ROUTE_NAMES.has(from.name) || !MANAGER_COMPANY_RECORD_ROUTE_NAMES.has(to.name)) {
        return false;
    }
    return (
        String(from.params.clientId ?? '') === String(to.params.clientId ?? '') &&
        String(from.params.companyId ?? '') === String(to.params.companyId ?? '')
    );
}

const routes = [
    {
        path: '/',
        component: () => import('../views/PublicAuthLayout.vue'),
        meta: { requiresAuth: false, publicAuthShell: true },
        children: [
            {
                // App start — choose Client or Vendor login
                path: '',
                name: 'MainHome',
                component: () => import('../views/MainHome.vue'),
                meta: { requiresAuth: false, publicAuthShell: true, isMainHome: true }
            },
            {
                path: 'login',
                name: 'Login',
                component: () => import('../views/Login.vue'),
                meta: { requiresAuth: false, publicAuthShell: true, isClientAuth: true }
            },
            {
                path: 'signup',
                name: 'Signup',
                component: () => import('../views/Signup.vue'),
                meta: { requiresAuth: false, publicAuthShell: true, isClientAuth: true }
            },
            {
                path: 'vendor/login',
                name: 'VendorLogin',
                component: () => import('../modules/vendor-portal/views/VendorLogin.vue'),
                meta: { requiresAuth: false, publicAuthShell: true, isVendorAuth: true }
            }
        ]
    },
    {
        path: '/legal',
        component: () => import('../views/LegalLayout.vue'),
        redirect: '/terms',
        meta: { requiresAuth: false, publicLanding: true },
        children: [
            {
                path: '/terms',
                name: 'Terms',
                component: () => import('../views/Terms.vue'),
                meta: { requiresAuth: false, publicLanding: true }
            },
            {
                path: '/privacy',
                name: 'Privacy',
                component: () => import('../views/Privacy.vue'),
                meta: { requiresAuth: false, publicLanding: true }
            },
            {
                path: '/support',
                name: 'Support',
                component: () => import('../views/Support.vue'),
                meta: { requiresAuth: false, publicLanding: true }
            }
        ]
    },
    {
        path: '/vendor',
        component: () => import('../modules/vendor-portal/views/VendorPortalLayout.vue'),
        redirect: '/vendor/home',
        meta: { requiresAuth: false, isVendorPortal: true },
        children: [
            {
                path: 'home',
                name: 'VendorHome',
                component: () => import('../modules/vendor-portal/views/VendorHome.vue'),
                meta: { requiresAuth: false, isVendorPortal: true, vendorRequiresAuth: true }
            },
            {
                path: 'upload-bill',
                name: 'VendorUploadBill',
                component: () => import('../modules/vendor-portal/views/VendorUploadBill.vue'),
                meta: { requiresAuth: false, isVendorPortal: true, vendorRequiresAuth: true }
            },
            {
                path: 'bills',
                name: 'VendorTrackBills',
                component: () => import('../modules/vendor-portal/views/VendorTrackBills.vue'),
                meta: { requiresAuth: false, isVendorPortal: true, vendorRequiresAuth: true }
            },
            {
                path: 'payments',
                name: 'VendorTrackPayments',
                component: () => import('../modules/vendor-portal/views/VendorTrackPayments.vue'),
                meta: { requiresAuth: false, isVendorPortal: true, vendorRequiresAuth: true }
            },
            {
                path: 'profile',
                name: 'VendorProfile',
                component: () => import('../modules/vendor-portal/views/VendorProfile.vue'),
                meta: { requiresAuth: false, isVendorPortal: true, vendorRequiresAuth: true }
            }
        ]
    },
    {
        path: '/accept-invite/:legacyInvite?',
        name: 'AcceptInvite',
        component: () => import('../views/AcceptInvite.vue'),
        meta: { requiresAuth: false }
    },
    {
        path: '/anna-admin/login',
        name: 'AdminLogin',
        component: () => import('../modules/Admin/views/AdminLogin.vue'),
        meta: { requiresAuth: false, isAdmin: true, isAdminAuth: true }
    },
    {
        path: '/admin/login',
        name: 'AdminLoginAlias',
        component: () => import('../modules/Admin/views/AdminLogin.vue'),
        meta: { requiresAuth: false, isAdmin: true, isAdminAuth: true }
    },
    {
        path: '/anna-admin/dashboard',
        name: 'AdminDashboard',
        component: () => import('../modules/Admin/views/AdminDashboard.vue'),
        meta: { requiresAuth: true, isAdmin: true }
    },
    {
        path: '/admin/dashboard',
        name: 'AdminDashboardAlias',
        component: () => import('../modules/Admin/views/AdminDashboard.vue'),
        meta: { requiresAuth: true, isAdmin: true }
    },
    {
        path: '/client-manager/login',
        name: 'ClientManagerLogin',
        component: () => import('../modules/Manager/views/ClientManagerLogin.vue'),
        meta: { requiresAuth: false, isClientManager: true, isManagerAuth: true }
    },
    {
        path: '/client-manager',
        component: ClientManagerLayout,
        meta: { requiresAuth: true, isClientManager: true },
        children: [
            {
                path: '',
                redirect: { name: 'ClientManagerDashboard' }
            },
            {
                path: 'dashboard',
                name: 'ClientManagerDashboard',
                component: ClientManagerDashboard,
                meta: { requiresAuth: true, isClientManager: true }
            },
            {
                path: 'clients/:clientId/company/:companyId',
                component: ManagerCompanyRecordsLayout,
                meta: { requiresAuth: true, isClientManager: true },
                children: [
                    {
                        path: 'consultants',
                        name: 'ManagerCompanyConsultants',
                        component: ManagerCompanyRecordsView,
                        meta: { requiresAuth: true, isClientManager: true }
                    },
                    {
                        path: 'customers',
                        name: 'ManagerCompanyCustomers',
                        component: ManagerCompanyRecordsView,
                        meta: { requiresAuth: true, isClientManager: true }
                    },
                    {
                        path: 'vendors',
                        name: 'ManagerCompanyVendors',
                        component: ManagerCompanyRecordsView,
                        meta: { requiresAuth: true, isClientManager: true }
                    },
                    {
                        path: 'invoices',
                        name: 'ManagerCompanyInvoices',
                        component: ManagerCompanyRecordsView,
                        meta: { requiresAuth: true, isClientManager: true }
                    },
                    {
                        path: 'timesheets',
                        name: 'ManagerCompanyTimesheets',
                        component: ManagerCompanyRecordsView,
                        meta: { requiresAuth: true, isClientManager: true }
                    },
                    {
                        path: 'bills',
                        name: 'ManagerCompanyBills',
                        component: ManagerCompanyRecordsView,
                        meta: { requiresAuth: true, isClientManager: true }
                    }
                ]
            }
        ]
    },
    {
        path: '/gmail-auth-result',
        name: 'GmailCallback',
        component: GmailCallback,
        meta: { requiresAuth: false }
    },
    {
        path: '/home',
        name: 'Home',
        component: () => import('../views/Home.vue'),
        meta: { requiresAuth: true }
    },
    {
        path: '/welcome-company',
        name: 'WelcomeCompany',
        component: WelcomeCompany,
        meta: { requiresAuth: true }
    },
    {
        path: '/company-setup',
        name: 'CreateCompany',
        component: CreateCompany,
        meta: { requiresAuth: true }
    },
    {
        path: '/company/:id/dashboard',
        component: Dashboard,
        meta: { requiresAuth: true },
        children: [
            ...settingsRoutes,
            ...customerRoutes,
            ...vendorRoutes,
            ...inventoryRoutes,
            ...consultantRoutes,
            ...reportsRoutes,
            ...bankingRoutes,
            ...companyRoutes,
            {
                path: 'overview',
                name: 'DashboardOverview',
                component: Overview
            },
            {
                path: 'tracker',
                name: 'Tracker',
                component: () => import('../views/dashboard/Tracker.vue')
            },
            {
                path: 'transactions',
                name: 'TransactionsCenter',
                component: () => import('../views/dashboard/TransactionsCenter.vue')
            },
            {
                path: 'importcsv',
                name: 'ImportFromFile',
                component: () => import('../views/dashboard/ImportFromFile.vue')
            },
            {
                path: 'import-staging',
                name: 'InvoiceImportStagingList',
                component: () => import('../views/dashboard/InvoiceImportStaging.vue')
            },
            {
                path: 'import-staging/:batchId',
                name: 'InvoiceImportStagingReview',
                component: () => import('../views/dashboard/InvoiceImportStaging.vue')
            },
            {
                path: 'receipt-import-staging',
                name: 'ReceiptImportStagingList',
                component: () => import('../views/dashboard/ReceiptImportStaging.vue')
            },
            {
                path: 'receipt-import-staging/:batchId',
                name: 'ReceiptImportStagingReview',
                component: () => import('../views/dashboard/ReceiptImportStaging.vue')
            },
            {
                path: 'credit-card-import-staging/:batchId',
                name: 'CreditCardImportStagingReview',
                component: () => import('../views/dashboard/CreditCardImportStaging.vue')
            },

            {
                path: 'company-preferences',
                name: 'CompanyPreferences',
                component: () => import('../modules/settings/views/CompanyPreferences.vue')
            },

        ]
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'NotFound',
        component: () => import('../views/NotFound.vue'),
        meta: { requiresAuth: false }
    }
]

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes
})

router.beforeEach(async (to, from, next) => {
    const isLoggedIn = store.state.auth.isLoggedIn
    const token = localStorage.getItem('token')
    const isAdminRoute = to.meta.isAdmin
    const isClientManagerRoute = to.matched.some((record) => record.meta.isClientManager)
    const adminToken = localStorage.getItem('adminToken')
    const adminIsLoggedIn = localStorage.getItem('adminIsLoggedIn') === 'true'
    const managerToken = localStorage.getItem('managerToken')
    const managerIsLoggedIn = localStorage.getItem('managerIsLoggedIn') === 'true'

    const isLegalPageSwitch =
      from.meta.publicLanding === true &&
      to.meta.publicLanding === true;
    const isPublicAuthSwitch =
      from.meta.publicAuthShell === true &&
      to.meta.publicAuthShell === true;
    const isVendorPortalSwitch =
      from.matched.some((record) => record.meta.isVendorPortal) &&
      to.matched.some((record) => record.meta.isVendorPortal) &&
      from.name !== 'VendorLogin' &&
      to.name !== 'VendorLogin';
    const skipRouteShimmer =
      isLegalPageSwitch ||
      isPublicAuthSwitch ||
      isVendorPortalSwitch ||
      isManagerCompanyRecordsTabSwitch(from, to) ||
      pageWindowNavigationService.isWindowTabSwitch(from, to);
    if (!skipRouteShimmer) {
        store.commit('setLoading', true)
    }

    try {
        // Handle client manager routes separately
        if (isClientManagerRoute) {
            const managerRouteRequiresAuth = to.matched.some((record) => record.meta.requiresAuth)
            if (managerRouteRequiresAuth) {
                // Client manager route requires auth
                if (!managerToken || !managerIsLoggedIn) {
                    next('/client-manager/login')
                    return
                }
                // Default manager: clients + companies only (no company records)
                if (MANAGER_COMPANY_RECORD_ROUTE_NAMES.has(to.name)) {
                    let isDefaultManager = false
                    try {
                        const managerUser = JSON.parse(localStorage.getItem('managerUser') || 'null')
                        isDefaultManager =
                            managerUser?.isDefaultManager === true ||
                            managerUser?.canAccessCompanyRecords === false
                    } catch (_) {
                        isDefaultManager = false
                    }
                    if (isDefaultManager) {
                        next({
                            path: '/client-manager/dashboard',
                            query: { tab: 'clients', focusClient: to.params.clientId }
                        })
                        return
                    }
                }
            } else {
                // Client manager login page - redirect if already logged in
                if (managerToken && managerIsLoggedIn) {
                    next('/client-manager/dashboard')
                    return
                }
            }
            next()
            return
        }

        // Public vendor login — reachable even with a client session open
        if (to.meta.isVendorAuth || to.name === 'VendorLogin') {
            const vendorToken = localStorage.getItem('vendorToken')
            const vendorUser = localStorage.getItem('vendorUser')
            if (vendorToken && vendorUser) {
                next('/vendor/home')
                return
            }
            next()
            return
        }

        const isVendorPortalRoute = to.matched.some((record) => record.meta.isVendorPortal)
        if (isVendorPortalRoute) {
            const vendorNeedsAuth = to.matched.some((record) => record.meta.vendorRequiresAuth)
            const vendorToken = localStorage.getItem('vendorToken')
            const vendorUser = localStorage.getItem('vendorUser')
            if (vendorNeedsAuth && (!vendorToken || !vendorUser)) {
                next('/vendor/login')
                return
            }
            next()
            return
        }

        // Handle admin routes separately
        if (isAdminRoute) {
            if (to.meta.requiresAuth) {
                // Admin route requires auth
                if (!adminToken || !adminIsLoggedIn) {
                    next('/anna-admin/login')
                    return
                }
                if (isAdminJwtExpired(adminToken)) {
                    clearAdminSession()
                    next({ path: '/anna-admin/login', query: { session: 'expired' } })
                    return
                }
            } else {
                // Admin login page - redirect if already logged in
                if (adminToken && adminIsLoggedIn) {
                    next('/anna-admin/dashboard')
                    return
                }
            }
            next()
            return
        }

        // If route requires auth (client routes)
        if (to.meta.requiresAuth) {
            // Check if user is logged in
            if (!isLoggedIn) {
                next('/login')
                return
            }

            // Verify token if it exists
            if (token) {
                try {
                    await store.dispatch('auth/verifyToken')
                } catch (error) {
                    // Token is invalid or expired
                    store.dispatch('logout')
                    next('/login')
                    return
                }
            } else {
                // No token found
                store.dispatch('logout')
                next('/login')
                return
            }
        }

        // Logged-in client on client auth pages → company books (/home).
        // MainHome stays the app entry (no auto /home). Legal + other role logins stay open.
        if (
            isLoggedIn &&
            !to.meta.requiresAuth &&
            to.name !== 'AcceptInvite' &&
            !to.meta.publicLanding &&
            !to.meta.isMainHome &&
            !to.meta.isVendorAuth &&
            !to.meta.isManagerAuth &&
            !to.meta.isAdminAuth
        ) {
            next('/home')
            return
        }

        next()
    } catch (error) {
        logger.error('Navigation error:', error)
        if (isClientManagerRoute) {
            next('/client-manager/login')
        } else if (isAdminRoute) {
            next('/anna-admin/login')
        } else {
            next('/login')
        }
    } finally {
        if (!skipRouteShimmer) {
            setTimeout(() => {
                store.commit('setLoading', false)
            }, 300)
        }
    }
})

// Set document title based on route
router.afterEach((to, from) => {
    const companySwitch = () => {
        try {
            if (!store.state.auth.isLoggedIn || !localStorage.getItem('token')) return;
            const toMatch = to.path.match(/^\/company\/(\d+)/);
            if (!toMatch) return;
            const companyId = parseInt(toMatch[1], 10);
            const fromMatch = from.path.match(/^\/company\/(\d+)/);
            const previousCompanyId = fromMatch ? parseInt(fromMatch[1], 10) : null;
            if (previousCompanyId && previousCompanyId !== companyId) {
                void store.dispatch('auth/reportCompanySwitch', { companyId, previousCompanyId });
            }
        } catch (e) {
            logger.error('Company switch log:', e);
        }
    };
    companySwitch();

    // Activity log: record when a user opens any Reports page
    try {
        if (store.state.auth.isLoggedIn && localStorage.getItem('token')) {
            // Only log when entering a different report (or first entry)
            if (to.name !== from.name || to.path !== from.path) {
                logReportVisitFromRoute(to);
            }
        }
    } catch (e) {
        logger.warn('Report visit log:', e);
    }

    // Set default title
    let title = 'Annabooks'
    
    // Add page-specific titles
    if (to.name) {
        switch (to.name) {
            case 'MainHome':
                title = 'AnnaBooks'
                break
            case 'Terms':
                title = 'AnnaBooks — Terms & Conditions'
                break
            case 'Privacy':
                title = 'AnnaBooks — Privacy Policy'
                break
            case 'Support':
                title = 'AnnaBooks — Support'
                break
            case 'Login':
                title = 'Annabooks - Client Login'
                break
            case 'Signup':
                title = 'Annabooks - Signup'
                break
            case 'AdminLogin':
                title = 'Annabooks - Admin Login'
                break
            case 'AdminDashboard':
                title = 'Annabooks - Admin Dashboard'
                break
            case 'VendorLogin':
                title = 'AnnaBooks — Vendor sign in'
                break
            case 'VendorHome':
                title = 'AnnaBooks — Vendor home'
                break
            case 'VendorProfile':
                title = 'AnnaBooks — Vendor profile'
                break
            case 'VendorTrackBills':
                title = 'AnnaBooks — Review invoices'
                break
            case 'VendorTrackPayments':
                title = 'AnnaBooks — Review receipts'
                break
            case 'VendorUploadBill':
                title = 'AnnaBooks — Submit invoice'
                break
            case 'ClientManagerLogin':
                title = 'Annabooks - Client Manager Login'
                break
            case 'ClientManagerDashboard':
                title = 'Annabooks - Client Manager Dashboard'
                break
            case 'Home':
                title = 'Annabooks - Home'
                break
            case 'DashboardOverview':
                title = 'Annabooks - Dashboard'
                break
            default:
                if (to.name.includes('Dashboard')) {
                    title = 'Dashboard - Annabooks'
                } else if (to.name) {
                    title = `Annabooks - ${to.name}`
                }
        }
    }
    
    document.title = title
})

export default router 